'use client';

import React from 'react';
import { CreditCard } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'current-status', title: 'Current Billing Status' },
  { id: 'free-plan', title: 'Free Plan Limits' },
  { id: 'pro-plan', title: 'Pro Plan (Coming Soon)' },
  { id: 'billing-terms', title: 'Billing Terms' },
  { id: 'refunds', title: 'Refund Policy' },
  { id: 'cancellation', title: 'Cancellation Policy' },
  { id: 'disputes', title: 'Billing Disputes' },
];

export default function RefundPolicyClient() {
  return (
    <PolicyLayout
      title="Refund & Billing Policy"
      description="JobCompanion's current billing structure, free plan limits, future premium plan terms, and refund and cancellation policies."
      lastUpdated="May 18, 2026"
      icon={CreditCard}
      iconBg="bg-success/10"
      iconColor="text-success"
      sections={sections}
      currentPath="/refund-policy"
    >
      <PolicyCallout type="info">
        JobCompanion is currently free to use during controlled beta. No payment is required. Premium plans are planned for a future release.
      </PolicyCallout>

      <PolicySection id="current-status" title="Current Billing Status">
        <p>
          JobCompanion is in controlled beta and is currently available at no cost. All registered users have access to the Free plan with the usage limits described below. No credit card is required to sign up.
        </p>
        <p>
          We will provide advance notice before any paid features are introduced. Existing beta users will receive clear communication about any changes to billing.
        </p>
      </PolicySection>

      <PolicySection id="free-plan" title="Free Plan Limits">
        <p>
          The Free plan includes the following monthly usage limits:
        </p>
        <PolicyList items={[
          '1 resume analysis per month',
          '20 job matches per month',
          '5 tracked applications per month',
          '3 AI-assisted workflows per month',
          '10 saved jobs',
        ]} />
        <p>
          Usage limits reset on the first day of each calendar month. Limits are designed to support genuine job seekers while protecting platform resources during beta.
        </p>
        <PolicyCallout type="info">
          If you reach a limit, you will see a friendly message explaining the limit and when it resets. We will not charge you or require payment to continue using free features.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="pro-plan" title="Pro Plan (Coming Soon)">
        <p>
          A Pro plan is planned for a future release. It will include higher usage limits and additional features. Planned Pro plan limits include:
        </p>
        <PolicyList items={[
          '50 resume analyses per month',
          '500 job matches per month',
          '100 tracked applications per month',
          '50 AI-assisted workflows per month',
          '500 saved jobs',
          'Priority job matching',
          'Advanced analytics and insights',
          'Early access to automation features',
        ]} />
        <p>
          Paid plans are not currently available. Pricing details will be announced before commercial launch. We will notify existing beta users in advance of any paid plan introduction.
        </p>
        <PolicyCallout type="warning">
          Do not make purchasing decisions based on planned features. Pro plan details are subject to change before release.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="billing-terms" title="Billing Terms (Future)">
        <p>
          When paid plans are introduced, the following terms will apply:
        </p>
        <PolicySubSection title="Subscription Billing">
          <PolicyList items={[
            'Subscriptions will be billed monthly or annually (annual plans may offer a discount)',
            'Billing occurs at the start of each billing period',
            'You will receive an email receipt for each charge',
            'Prices are listed in USD unless otherwise specified',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Payment Methods">
          <PolicyList items={[
            'Credit and debit cards (Visa, Mastercard, American Express)',
            'Additional payment methods may be added at launch',
            'Payment processing will be enabled when paid subscriptions become available.',
          ]} />
        </PolicySubSection>
      </PolicySection>

      <PolicySection id="refunds" title="Refund Policy">
        <p>
          During the current free beta, no payments are processed and no refunds are applicable.
        </p>
        <p>
          When paid plans are introduced, the following refund policy will apply:
        </p>
        <PolicyList items={[
          'Monthly subscriptions: No refunds for partial months. You may cancel at any time and retain access until the end of the current billing period.',
          'Annual subscriptions: Refunds may be available within 14 days of initial purchase if you have not used the plan features. Contact us to request a refund.',
          'Refunds will not be issued for accounts suspended due to Terms of Service violations.',
          'Refunds for technical issues preventing platform access will be evaluated on a case-by-case basis.',
        ]} />
        <p>
          To request a refund, contact us at <a href="mailto:billing@jobcompanion.app" className="text-primary underline">billing@jobcompanion.app</a> with your account email and reason for the request.
        </p>
      </PolicySection>

      <PolicySection id="cancellation" title="Cancellation Policy">
        <p>
          You may cancel your subscription at any time:
        </p>
        <PolicyList items={[
          'Cancellation takes effect at the end of the current billing period',
          'You retain access to Pro features until the period ends',
          'After cancellation, your account reverts to the Free plan',
          'Your data is retained for 30 days after cancellation before being subject to standard retention policies',
          'You can resubscribe at any time',
        ]} />
        <p>
          Cancellation instructions will be available in your account Settings page when paid plans are active.
        </p>
      </PolicySection>

      <PolicySection id="disputes" title="Billing Disputes">
        <p>
          If you believe you have been charged incorrectly, contact us within 30 days of the charge at <a href="mailto:billing@jobcompanion.app" className="text-primary underline">billing@jobcompanion.app</a>. Include:
        </p>
        <PolicyList items={[
          'Your account email address',
          'The date and amount of the disputed charge',
          'A description of the issue',
        ]} />
        <p>
          We will investigate and respond within 5 business days. If a billing error is confirmed, we will issue a correction or refund promptly.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
